/* 스토리 앨범 — 장소가 앨범(그룹)이고, 그 장소에서 만나는 장면 카드가 앨범에 든다(기획: 시나리오 기능을 없애고 분기별 컬렉션).
   장소마다 서버의 자유 진입 장면(work.branches, place 가 같은 것)이 있으면 "이 장면부터 대화"로 순서·진행과 관계없이 들어간다.
   그 장면에 묶인 카드(cards[].gate.branch)는 서버가 진입과 함께 지급한다.
   작가가 공개(branches[].public)한 앨범은 상세 화면에서 제목·표지를 미리 보여 주고, 나머지는 들어가 보거나 카드를 모을 때까지 흐리게 둔다. */
import { gridItems, workFor } from '@/features/veluvit/lib/rules'

import type { GridItem } from '@/features/veluvit/lib/rules'
import type {
  CardGuide,
  Room,
  WorkBranch,
} from '@/features/veluvit/types/api.types'
import type { Catalog } from '@/features/veluvit/types/catalog.types'

/** 장소 앨범에 붙는 이미지 생성 가이드 — 서버는 카드마다 cards[].guide 로 받는다 */
export type AlbumGuide = CardGuide

/** 스튜디오가 저장하는 장면 = 서버 branches 한 줄 + 상세 미리 공개 여부 */
export type AlbumBranch = WorkBranch & { public?: boolean }

type AlbumView = {
  id: string
  title: string
  place: string
  /** 들어갈 장면 — 없으면 이 앨범에서는 대화를 시작하지 않는다 */
  branch?: AlbumBranch
  items: GridItem[]
  /** 모은 카드 수 */
  owned: number
  /** 들어가 봤거나 카드를 하나라도 모음 */
  opened: boolean
  /** 제목·표지를 보여 줘도 됨 — 열었거나 작가가 공개 */
  revealed: boolean
  cover: string
}

/** 작가가 공개 여부를 정하지 않은 예전 작품은 앞의 두 앨범만 미리 공개한다 */
const DEFAULT_PUBLIC = 2

/**
 * 작품 앨범 목록 — 장소 순서는 작품 장소 목록, 그다음 카드·장면에만 있는 장소.
 * room 은 이 이용자의 방(들어가 본 장면), unlocked 는 모은 카드 키(`${workId}:${카드 순번}`).
 */
export function albumViews(
  catalog: Catalog,
  workId: string,
  room: Room | undefined,
  unlocked: Set<string>,
  fallbackCover: string
): AlbumView[] {
  const branches = (workFor(catalog, workId)?.branches || []) as AlbumBranch[]
  const items = gridItems(catalog, workId)
  const places = [
    ...(catalog.chars[workId]?.places || []).map((p) => p.name),
    ...items.map((item) => item.place),
    ...branches.map((b) => b.place || ''),
  ]
  const order = Array.from(new Set(places.filter(Boolean)))
  const visited = new Set(room?.branchVisits || [])
  return order
    .map((place, index) => {
      const branch = branches.find((b) => b.place === place)
      const own = items.filter(
        (item) =>
          item.place === place || (branch && item.gate.branch === branch.id)
      )
      const owned = own.filter((item) =>
        unlocked.has(`${workId}:${item.i}`)
      ).length
      const opened = owned > 0 || (!!branch && visited.has(branch.id))
      return {
        id: branch?.id || `place:${place}`,
        title: branch?.title || place,
        place,
        branch,
        items: own,
        owned,
        opened,
        revealed: opened || (branch?.public ?? index < DEFAULT_PUBLIC),
        cover: own[0]?.img || fallbackCover,
      }
    })
    .filter((album) => album.items.length > 0 || album.branch)
}
